import React from 'react';
import {
  CapEnvironmentService,
  useCapEnvironmentsService,
} from '@cengage/cap-portal-environment-lib-react';
import axios from 'axios';

import {
  CourseUri,
  UserIdentifier,
} from './portal-core-data/portal-core-data.model';
import { ManifestService, useManifestService } from './manifest.service';
import { ErrorService, useErrorService } from './error.service';

export enum ClientInteractionEnvironmentKeys {
  ClientInteractionEndpoint = 'clientInteractionEndpoint',
}

export class CapClientInteractionService {
  private requestedUserCoursePairs: {
    [key: string]: { [key: string]: boolean };
  } = {};

  constructor(
    private manifestService: ManifestService,
    private environmentService: CapEnvironmentService,
    private errorService: ErrorService
  ) {}

  wasUserRecentlyRequested(
    courseUri: CourseUri,
    userIdentifier: UserIdentifier
  ): boolean {
    return Boolean(
      (this.requestedUserCoursePairs[courseUri] || {})[userIdentifier]
    );
  }

  requestReport(
    courseUri: CourseUri,
    userIdentifier: UserIdentifier,
    courseShortLabel: string,
    userFirstName: string,
    userLastName: string
  ): void {
    if (!this.requestedUserCoursePairs[courseUri]) {
      this.requestedUserCoursePairs[courseUri] = {};
    }
    if (!this.requestedUserCoursePairs[courseUri][userIdentifier]) {
      this.requestedUserCoursePairs[courseUri][userIdentifier] = true;
      axios
        .post(
          `${this.environmentService.getEnvironmentSetting(
            ClientInteractionEnvironmentKeys.ClientInteractionEndpoint
          )}/subscribe-student/${encodeURIComponent(
            courseUri
          )}/${encodeURIComponent(userIdentifier)}`
        )
        .then(
          () => {
            this.manifestService.addRequestedManifestEntry(
              `report:client-interaction:${courseUri}:${userIdentifier}`,
              `${courseShortLabel} - ${userFirstName} ${userLastName}`,
              'User Interaction Log',
              'report-type:client-interaction:v1'
            );
          },
          () => {
            delete this.requestedUserCoursePairs[courseUri][userIdentifier];
            this.errorService.handleError(
              `Unable to request Client Interaction Report for ${courseUri} ${userLastName}, ${userFirstName}`
            );
          }
        );
    }
  }
}

export const CLIENT_INTERACTION_SERVICE =
  React.createContext<CapClientInteractionService>(null!);

export function ClientInteractionServiceProvider({
  children,
}: React.PropsWithChildren) {
  const manifestService = useManifestService();
  const environmentService = useCapEnvironmentsService();
  const errorService = useErrorService();
  const [clientInteractionService] = React.useState(
    () =>
      new CapClientInteractionService(
        manifestService,
        environmentService,
        errorService
      )
  );

  return (
    <CLIENT_INTERACTION_SERVICE.Provider value={clientInteractionService}>
      {children}
    </CLIENT_INTERACTION_SERVICE.Provider>
  );
}

export function useClientInteractionService() {
  return React.useContext(CLIENT_INTERACTION_SERVICE);
}
